import { cbSemanticCssVars, colors, typographyCss } from '@creditornot/cb-ingredients';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { Body1, Body3, Heading5 } from 'common/typography/Typography';
import { SsmoTask, SsmoTaskStatus, taskStatusToSubtitleMapping } from 'modules/onboarding-page/constants';
import { SsmoTaskStep } from 'modules/onboarding-page/types';
import { getKybStatusId, calculateTaskProgress, getContractSigningStatusId } from 'modules/onboarding-page/utils';

import CircularProgress from '../../ProgressCircle';
import CompletedIcon from '../icons/Completed';
import { Stage } from '../types';

type OnboardingStagesProps = {
  stages: Stage[];
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const StepDividerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StepContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
`;

const StepNumberContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border: 1px solid ${cbSemanticCssVars.borderPrimary};
  border-radius: 50%;
  box-shadow: 0 0 1px 0 #0000001f;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 32px;
    height: 32px;
  }
`;

const StepNumber = styled(Heading5)`
  font-weight: 600;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    ${typographyCss.Heading6}
  }
`;

const Divider = styled.div`
  width: 1px;
  border-inline-start: 1px dashed ${cbSemanticCssVars.borderPrimary};
  flex-grow: 1;
`;

const StepContent = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 20px;
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StepHeader = styled(Heading5)`
  font-weight: 600;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    ${typographyCss.Heading6};
    font-weight: 600;
  }
`;

const StepSubheader = styled(Body3)`
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    ${typographyCss.Small()};
  }
`;

const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-block-end: 48px;
  gap: 16px;
`;

const CardTitle = styled(Body1)`
  font-weight: 700;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    ${typographyCss.Body3()};
    font-weight: 700;
  }
`;

const Card = styled.div<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  padding: 24px;
  border: 1px solid ${cssThemedValue({ light: colors.pepper4, dark: colors.salt36 })};
  border-radius: 16px;
  gap: 16px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  box-shadow: 0 1px 2px 0 #0000001f;
  /* TODO when new colors available: bg-surface-secondary */
  background-color: ${({ disabled }) =>
    disabled
      ? cssThemedValue({ light: colors.pepper4, dark: colors.salt8 })
      : cssThemedValue({ light: colors.salt100, dark: colors.pepper2 })};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    border-radius: 12px;
    padding: 16px;
    gap: 12px;
  }
`;

const IconContainer = styled.div`
  padding: 12px;
  border-radius: 8px;
  background-color: ${cssThemedValue({ light: colors.pepper4, dark: colors.salt4 })};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 8px;
    border-radius: 6px;
  }
`;

const Icon = styled.div`
  display: flex;
  align-items: center;
  width: 20px;
  height: 20px;
  fill: ${colors.wolt100};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 16px;
    height: 16px;
  }
`;

const CardText = styled.div`
  display: flex;
  flex-direction: column;
`;

const StatusIcon = styled.div`
  display: flex;
  flex-direction: column;
  margin-inline-start: auto;
`;

const CardSubtitle = styled(Body3)`
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    ${typographyCss.Small()};
  }
`;

const StyledCompletedIcon = styled(CompletedIcon)`
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 22px;
    height: 22px;
  }
`;

const OnboardingStages = ({ stages }: OnboardingStagesProps) => {
  const displayTaskStatus = (taskType: SsmoTask, taskStatus: SsmoTaskStatus, steps: SsmoTaskStep[]) => {
    if (taskType === SsmoTask.KYB) {
      return <FormattedMessage id={getKybStatusId(taskStatus, steps)} />;
    }

    if (taskType === SsmoTask.CONTRACT_SIGNING) {
      return <FormattedMessage id={getContractSigningStatusId(taskStatus, steps)} />;
    }

    return <FormattedMessage id={taskStatusToSubtitleMapping[taskStatus]} />;
  };

  return (
    <Container>
      {stages.map((stage, index) => {
        const hasCustomComponent = stage.customComponent !== undefined;

        return (
          <StepContainer key={index}>
            <StepDividerContainer>
              <StepNumberContainer>
                <StepNumber>{stage.number}</StepNumber>
              </StepNumberContainer>

              {!hasCustomComponent && <Divider />}
            </StepDividerContainer>

            <StepContent>
              <HeaderContainer>
                <StepHeader>{stage.header}</StepHeader>
                <StepSubheader>{stage.description}</StepSubheader>
              </HeaderContainer>

              {hasCustomComponent ? (
                stage.customComponent
              ) : (
                <CardsContainer>
                  {stage.tasks.map(
                    ({ status, onStart, type, taskSteps, title, duration, icon, disabled }, taskIndex) => (
                      <Card key={taskIndex} onClick={onStart} disabled={disabled}>
                        <IconContainer>
                          <Icon>{icon}</Icon>
                        </IconContainer>

                        <CardText>
                          <CardTitle>{title}</CardTitle>
                          <CardSubtitle>
                            {status === SsmoTaskStatus.BLOCKED || status === SsmoTaskStatus.OPEN
                              ? duration
                              : displayTaskStatus(type, status, taskSteps)}
                          </CardSubtitle>
                        </CardText>
                        <StatusIcon>
                          {status === SsmoTaskStatus.IN_PROGRESS && (
                            <CircularProgress progress={calculateTaskProgress(taskSteps)} />
                          )}
                          {status === SsmoTaskStatus.COMPLETED && <StyledCompletedIcon />}
                        </StatusIcon>
                      </Card>
                    ),
                  )}
                </CardsContainer>
              )}
            </StepContent>
          </StepContainer>
        );
      })}
    </Container>
  );
};

export default OnboardingStages;
